import type { WalkthroughStep } from '../../store/walkthroughStore';
import { navTourSelector, sectionTourSelector } from './tourPrefs';

export type TourProfile =
  | 'DIRETOR'
  | 'SECRETARIA'
  | 'COORDENADOR'
  | 'PROFESSOR'
  | 'RESPONSAVEL'
  | 'ALUNO'
  | 'PREFEITO'
  | 'MUNICIPAL'
  | 'ESTADUAL'
  | 'MASTER'
  | 'ADMIN'
  | 'ESCOLA';

function step(
  id: string,
  ordem: number,
  selectorCss: string,
  legenda: string,
  subtopicoTitulo: string,
  urlAtual: string,
): WalkthroughStep {
  return {
    id,
    subtopicoId: id.split('.')[0] || 'tour',
    ordem,
    tipoAcao: 'HIGHLIGHT',
    selectorCss,
    legenda,
    urlAtual,
    audioUrl: null,
    screenshotUrl: null,
    subtopicoTitulo,
  };
}

/** Resolve perfil de tour a partir do auth (ESCOLA+cargo → DIRETOR/SECRETARIA/…). */
export function resolveTourProfile(
  perfil?: string | null,
  cargo?: string | null,
): TourProfile {
  const p = (perfil || '').toUpperCase();
  const c = (cargo || '').toUpperCase();
  if (p === 'ESCOLA') {
    if (c === 'SECRETARIA') return 'SECRETARIA';
    if (c === 'COORDENACAO' || c === 'COORDENADOR') return 'COORDENADOR';
    return 'DIRETOR';
  }
  if (p === 'MUNICIPAL') return 'PREFEITO';
  if (p === 'ADMIN') return 'MASTER';
  if (
    p === 'DIRETOR' ||
    p === 'SECRETARIA' ||
    p === 'COORDENADOR' ||
    p === 'PROFESSOR' ||
    p === 'RESPONSAVEL' ||
    p === 'ALUNO' ||
    p === 'PREFEITO' ||
    p === 'ESTADUAL' ||
    p === 'MASTER'
  ) {
    return p;
  }
  return 'DIRETOR';
}

export function guessHome(profile: TourProfile): string {
  switch (profile) {
    case 'SECRETARIA':
    case 'COORDENADOR':
      return '/equipe/dashboard';
    case 'PROFESSOR':
      return '/professor/dashboard';
    case 'RESPONSAVEL':
      return '/responsavel/dashboard';
    case 'ALUNO':
      return '/aluno/dashboard';
    case 'PREFEITO':
    case 'MUNICIPAL':
      return '/municipal/dashboard';
    case 'ESTADUAL':
      return '/estadual/dashboard';
    case 'MASTER':
    case 'ADMIN':
      return '/master/dashboard';
    default:
      return '/admin/dashboard';
  }
}

export function getBuiltInTourId(profile: TourProfile, pathname?: string): string {
  const home = pathname || guessHome(profile);
  return `${profile.toLowerCase()}:${home}`;
}

function welcomeStep(
  profile: TourProfile,
  url: string,
  title: string,
  text: string,
): WalkthroughStep {
  return step(`${profile}.welcome`, 1, '', text, title, url);
}

function buildSidebarTour(
  profile: TourProfile,
  url: string,
  items: Array<{ to?: string; section?: string; title: string; text: string }>,
): WalkthroughStep[] {
  const steps: WalkthroughStep[] = [
    welcomeStep(
      profile,
      url,
      'Bem-vindo',
      'Tour rápido do seu painel. Você pode pular, desativar em Ajuda (?) ou chamar de novo quando quiser.',
    ),
  ];
  let ordem = 2;
  for (const item of items) {
    const selector = item.section
      ? sectionTourSelector(item.section)
      : item.to
        ? navTourSelector(item.to)
        : '';
    steps.push(
      step(`${profile}.s${ordem}`, ordem, selector, item.text, item.title, url),
    );
    ordem += 1;
  }
  steps.push(
    step(
      `${profile}.fim`,
      ordem,
      '',
      'Pronto! Abra Ajuda (?) para refazer o tour ou desligar a orientação automática.',
      'Fim do tour',
      url,
    ),
  );
  return steps;
}

const TOUR_BUILDERS: Record<TourProfile, () => WalkthroughStep[]> = {
  DIRETOR: () =>
    buildSidebarTour('DIRETOR', '/admin/dashboard', [
      { to: '/admin/dashboard', title: 'Dashboard', text: 'Aqui você acompanha a operação do dia: chamadas, portaria e alertas.' },
      { section: 'Escola', title: 'Menu Escola', text: 'Cadastros de pessoas e base da escola (alunos, turmas, equipe…).' },
      { section: 'Operacional', title: 'Operacional', text: 'Rotina do dia: chamada, cobertura de grade, comunicados e mídias.' },
      { to: '/admin/chamada', title: 'Chamada', text: 'Acompanhe e gerencie as chamadas das turmas.' },
      { to: '/admin/cobertura', title: 'Cobertura', text: 'Afaste professores e aloque substitutos na grade.' },
      { to: '/admin/comunicados', title: 'Comunicados', text: 'Envie avisos para famílias e professores.' },
    ]),
  SECRETARIA: () =>
    buildSidebarTour('SECRETARIA', '/equipe/dashboard', [
      { to: '/equipe/dashboard', title: 'Dashboard', text: 'Painel operacional da equipe escolar.' },
      { section: 'Pessoas', title: 'Pessoas', text: 'Alunos, responsáveis e equipe.' },
      { section: 'Operação do Dia', title: 'Operação do Dia', text: 'Cobertura, portaria, incidentes e comunicados.' },
      { to: '/equipe/cobertura', title: 'Cobertura', text: 'Apoie a cobertura de grade quando houver afastamentos.' },
      { to: '/equipe/comunicados', title: 'Comunicados', text: 'Comunique famílias e professores.' },
    ]),
  COORDENADOR: () =>
    buildSidebarTour('COORDENADOR', '/equipe/dashboard', [
      { to: '/equipe/dashboard', title: 'Dashboard', text: 'Visão da coordenação pedagógica e operação do dia.' },
      { section: 'Frequência & Turmas', title: 'Frequência', text: 'Turmas, frequência e governança de chamadas.' },
      { section: 'Operação do Dia', title: 'Operação', text: 'Cobertura de grade, portaria e incidentes.' },
      { to: '/equipe/cobertura', title: 'Cobertura', text: 'Organize substituições na grade horária.' },
    ]),
  PROFESSOR: () =>
    buildSidebarTour('PROFESSOR', '/professor/dashboard', [
      { to: '/professor/dashboard', title: 'Dashboard', text: 'Sua sala: turmas, chamada e avisos do dia.' },
      { section: 'Dia a Dia', title: 'Dia a Dia', text: 'Chamada, diário e comunicados ficam neste bloco.' },
      { to: '/professor/chamada', title: 'Chamada', text: 'Registre a presença da turma.' },
      { to: '/professor/comunicados', title: 'Comunicados', text: 'Veja avisos da escola.' },
    ]),
  RESPONSAVEL: () =>
    buildSidebarTour('RESPONSAVEL', '/responsavel/dashboard', [
      { to: '/responsavel/dashboard', title: 'Início', text: 'Portal da família: acompanhe seus dependentes.' },
      { to: '/responsavel/filhos', title: 'Meus Filhos', text: 'Veja a vida escolar de cada dependente.' },
      { to: '/responsavel/comunicados', title: 'Comunicados', text: 'Avisos enviados pela escola.' },
      { to: '/responsavel/notificacoes', title: 'Notificações', text: 'Alertas e lembretes em tempo real.' },
    ]),
  ALUNO: () =>
    buildSidebarTour('ALUNO', '/aluno/dashboard', [
      { to: '/aluno/dashboard', title: 'Início', text: 'Seu painel: atalhos para grade, frequência e avisos.' },
      { to: '/aluno/grade', title: 'Grade', text: 'Consulte horários e matérias.' },
      { to: '/aluno/frequencia', title: 'Frequência', text: 'Acompanhe sua presença.' },
      { to: '/aluno/comunicados', title: 'Comunicados', text: 'Avisos da escola.' },
    ]),
  PREFEITO: () =>
    buildSidebarTour('PREFEITO', '/municipal/dashboard', [
      { to: '/municipal/dashboard', title: 'Dashboard', text: 'Visão municipal da rede de escolas.' },
      { to: '/municipal/escolas', title: 'Escolas', text: 'Acompanhe unidades da rede.' },
      { to: '/municipal/comunicados', title: 'Comunicados', text: 'Comunicados para escolas e famílias.' },
    ]),
  MUNICIPAL: () => TOUR_BUILDERS.PREFEITO(),
  ESTADUAL: () =>
    buildSidebarTour('ESTADUAL', '/estadual/dashboard', [
      { to: '/estadual/dashboard', title: 'Dashboard', text: 'Painel estadual da educação.' },
      { section: 'Visao Estadual', title: 'Visão Estadual', text: 'Municípios, escolas e alunos do estado.' },
      { to: '/estadual/municipios', title: 'Municípios', text: 'Rede de municípios sob gestão estadual.' },
    ]),
  MASTER: () =>
    buildSidebarTour('MASTER', '/master/dashboard', [
      { to: '/master/dashboard', title: 'Central', text: 'Comando master da plataforma.' },
      { to: '/master/customizacao', title: 'Customização', text: 'White-label e cores por perfil.' },
    ]),
  ADMIN: () => TOUR_BUILDERS.MASTER(),
  ESCOLA: () => TOUR_BUILDERS.DIRETOR(),
};

function isHomePath(profile: TourProfile, pathname: string): boolean {
  const home = guessHome(profile);
  const path = pathname.replace(/\/+$/, '') || '/';
  const root = home.replace(/\/dashboard$/, '');
  return path === home || path === root;
}

/** Tour para auto-start (somente na home do perfil). */
export function getBuiltInTour(
  profile: TourProfile,
  pathname: string,
): { tourId: string; steps: WalkthroughStep[] } | null {
  if (!isHomePath(profile, pathname)) return null;
  const steps = TOUR_BUILDERS[profile]?.();
  if (!steps?.length) return null;
  return { tourId: getBuiltInTourId(profile), steps };
}

/** Tour sob demanda (Ajuda) — sempre o tour da home do perfil. */
export function getBuiltInTourForHelp(
  profile: TourProfile,
): { tourId: string; steps: WalkthroughStep[] } | null {
  const steps = TOUR_BUILDERS[profile]?.();
  if (!steps?.length) return null;
  return { tourId: getBuiltInTourId(profile), steps };
}
